import { VOMelodyPair } from "../model/DataStructure.js";
import { Labels } from "../model/Labels.js";
import { Decoration } from "./Decoration.js";
import { Polygon, PolygonShape } from "./Polygon.js";

export interface MelodyInstrumentSelectorOptions {
    container: HTMLElement;
    visible?: boolean;
    labels: Labels;
    items: VOMelodyPair[];
}

export class MelodyInstrumentSelector {

    public onSelect = e5.Signal.create<[VOMelodyPair]>();

    private _options: MelodyInstrumentSelectorOptions;
    private _element: HTMLDivElement;
    private _decoration: Decoration;
    private _items: MelodyInstrumentSelectorItem[] = [];

    constructor(options: MelodyInstrumentSelectorOptions) {
        options = e5.object.merge({
            visible: true
        }, options);
        this._options = options;

        this._element = document.createElement("div");
        this._element.className = "melody-instrument-selector";
        this._element.innerHTML = `
        <div class="melody-instrument-selector__decorations"></div>
        <div class="melody-instrument-selector__item-list"></div>
        <div class="melody-instrument-selector__title">${options.labels.html("melody.melody_instrument_selector.title")}</div>`;

        this._decoration = new Decoration({
            container: this._element.querySelector(".melody-instrument-selector__decorations"),
            visible: options.visible,
            names: [
                "melody-instrument-selector-1",
                "melody-instrument-selector-2",
                "melody-instrument-selector-10",
                "melody-instrument-selector-12",
                "melody-instrument-selector-13"
            ]
        });

        const shapeVariations: PolygonShape[] = ["1", "3", "4", "5"];
        for (let i = 0; i < options.items.length; ++i) {
            this._items.push(
                new MelodyInstrumentSelectorItem({
                    shape: shapeVariations[i % shapeVariations.length],
                    item: options.items[i],
                    onClick: (instrument) => {
                        this.onSelect.dispatch(instrument);
                    },
                    container: this._element.querySelector(".melody-instrument-selector__item-list")
                })
            );
        }

        this.visible = options.visible;

        if (options.container) {
            options.container.appendChild(this._element);
        }
    }

    public get visible(): boolean {
        return !this._element.classList.contains("melody-instrument-selector--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this._element.classList.toggle("melody-instrument-selector--hidden", !value);
        this._decoration.visible = value;
    }
}

interface MelodyInstrumentSelectorItemOptions {
    item: VOMelodyPair;
    container: HTMLElement;
    shape: PolygonShape;
    onClick?: (orchestra: VOMelodyPair) => void;
}

class MelodyInstrumentSelectorItem {
    public readonly element: HTMLButtonElement;
    public readonly item: VOMelodyPair;
    public onClick = e5.Signal.create<[VOMelodyPair]>();

    private _polygon: Polygon;

    constructor(options: MelodyInstrumentSelectorItemOptions) {
        this.item = options.item;

        this.element = document.createElement("button");
        this.element.className = "melody-instrument-selector__item";
        this.element.innerHTML = `
        <div class="melody-instrument-selector__item-logo">
             <img class="melody-instrument-selector__item-img" src="${options.item.thumbnail.url}"></img>
        </div>
        <div class="melody-instrument-selector__item-name">
            ${e5.Text.escapeHtml(options.item.name)}
        </div>
        `;

        this.element.style.setProperty("--accent-color", options.item.color);

        this._polygon = new Polygon({
            container: this.element,
            shadowDistance: 40,
            prepend: true,
            background: false,
            shape: options.shape,
            gradient: false,
            shadow: true
        });

        if (typeof options.onClick === "function") {
            this.onClick.add(options.onClick);
        }

        this.element.addEventListener("click", () => {
            this.onClick.dispatch(this.item);
        });

        options.container.appendChild(this.element);
    }

    public get selected(): boolean {
        return this.element.classList.contains("melody-instrument-selector__item--selected");
    }

    public set selected(value: boolean) {
        if (this.selected === value) {
            return;
        }
        this.element.classList.toggle("melody-instrument-selector__item--selected", value);
        this.element.disabled = value;
    }
}